/** Phase 2B — Assets page.
 * Two sub-tabs: Library (grid, upload, edit drawer) + Collections (list/detail).
 */
import { useEffect, useMemo, useState } from "react";
import { api, auth } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import AssetUploadDialog from "./AssetUploadDialog";
import AssetEditDrawer from "./AssetEditDrawer";
import AssetPicker from "./AssetPicker";
import ProvenanceDrawer from "../../components/app/ProvenanceDrawer";

const KINDS = ["image", "video", "copy", "landing_page_ref", "prompt", "document", "audio"];
const STATUSES = ["draft", "approved", "rejected", "archived"];
const STATUS_VARIANTS = {
  draft: "secondary", approved: "default",
  rejected: "destructive", archived: "outline",
};
const ROLES_CAN_WRITE = new Set(["owner", "admin", "manager", "producer"]);

function kindBadge(k) { return k; }

export default function Assets() {
  const [tab, setTab] = useState(
    new URLSearchParams(window.location.search).get("tab") || "library"
  );
  return (
    <div data-testid="assets-page" className="space-y-6">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Assets</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Brand & campaign assets — upload, organise, attach to campaigns.
        </p>
      </div>
      <Tabs value={tab} onValueChange={(v) => {
        setTab(v); window.history.replaceState(null, "", `?tab=${v}`);
      }}>
        <TabsList>
          <TabsTrigger value="library" data-testid="assets-tab-library">Library</TabsTrigger>
          <TabsTrigger value="collections" data-testid="assets-tab-collections">Collections</TabsTrigger>
        </TabsList>
        <TabsContent value="library"><Library /></TabsContent>
        <TabsContent value="collections"><Collections /></TabsContent>
      </Tabs>
    </div>
  );
}

function Library() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const [status, setStatus] = useState("all");
  const [editId, setEditId] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [provenanceId, setProvenanceId] = useState(null);
  const role = (auth.getUser() || {}).role || "";
  const canWrite = ROLES_CAN_WRITE.has(role);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (kind !== "all") params.kind = kind;
    if (status !== "all") params.status = status;
    try {
      const r = await api.get("/assets", { params });
      setItems(r.data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, status, q]);

  // Phase 4.4 — deep-link `?asset_id=<id>`: scroll into view + ring highlight
  // for ~2s, then open the edit drawer. Only fires once per mount.
  useEffect(() => {
    if (loading || !items.length) return;
    const deepId = new URLSearchParams(window.location.search).get("asset_id");
    if (!deepId) return;
    const exists = items.some((x) => x.id === deepId);
    if (!exists) return;
    setHighlightId(deepId);
    setEditId(deepId);
    requestAnimationFrame(() => {
      const el = document.querySelector(
        `[data-testid="asset-card-${deepId}"]`);
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
    const tm = setTimeout(() => setHighlightId(null), 2000);
    return () => clearTimeout(tm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, items.length]);

  return (
    <Card className="mt-3">
      <CardContent className="pt-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <Input data-testid="assets-search" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger data-testid="assets-kind-filter"><SelectValue placeholder="Kind" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All kinds</SelectItem>
              {KINDS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="assets-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} data-testid="assets-refresh">Refresh</Button>
          {canWrite && (
            <Button onClick={() => setUploadOpen(true)} data-testid="assets-upload-open">
              + Upload Asset
            </Button>
          )}
        </div>

        {loading ? (
          <div className="text-sm text-muted-foreground py-6">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-muted-foreground py-12 text-center" data-testid="assets-empty">
            No assets match the current filter.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((a) => (
              <button
                key={a.id}
                type="button"
                data-testid={`asset-card-${a.id}`}
                data-highlight={highlightId === a.id ? "true" : undefined}
                onClick={() => setEditId(a.id)}
                className={[
                  "text-left rounded-lg border bg-card hover:bg-accent/40",
                  "transition-colors p-4 flex flex-col gap-3 min-h-[180px]",
                  highlightId === a.id
                    ? "ring-2 ring-primary ring-offset-2 ring-offset-background"
                    : "",
                ].join(" ")}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="font-medium leading-tight pr-2 truncate">{a.name}</div>
                  <div className="flex items-center gap-1">
                    {a.ai_generated && (
                      <span
                        role="button"
                        tabIndex={0}
                        data-testid={`asset-ai-chip-${a.id}`}
                        onClick={(e) => { e.stopPropagation(); setProvenanceId(a.id); }}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setProvenanceId(a.id); } }}
                        className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-violet-600 text-white cursor-pointer hover:bg-violet-700"
                        title="AI-generated · view provenance"
                      >AI</span>
                    )}
                    <Badge variant={STATUS_VARIANTS[a.status] || "outline"} data-testid={`asset-status-${a.status}`}>{a.status}</Badge>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <Badge variant="outline" className="font-mono">{kindBadge(a.kind)}</Badge>
                  {a.language && <Badge variant="outline" className="uppercase">{a.language}</Badge>}
                  <span className="text-muted-foreground ml-auto">v{a.version}</span>
                </div>
                {a.kind === "image" && a.preview_url && (
                  <div className="rounded-md overflow-hidden border bg-muted/30 aspect-[3/2]">
                    <img src={a.preview_url} alt={a.name} className="w-full h-full object-cover" />
                  </div>
                )}
                {(a.kind === "copy" || a.kind === "prompt") && a.text_content && (
                  <p className="text-xs text-muted-foreground line-clamp-3 font-sans">{a.text_content}</p>
                )}
                {(a.tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {a.tags.slice(0, 4).map((t) => (
                      <span key={t} className="text-[10px] px-2 py-0.5 rounded-full border bg-background text-muted-foreground">{t}</span>
                    ))}
                  </div>
                )}
                <UsageFooter usage={a.usage} />
              </button>
            ))}
          </div>
        )}
      </CardContent>

      <AssetUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onUploaded={load} />
      <AssetEditDrawer
        assetId={editId}
        open={!!editId}
        onClose={() => setEditId(null)}
        onChanged={load}
      />
      <ProvenanceDrawer
        assetId={provenanceId}
        open={!!provenanceId}
        onClose={() => setProvenanceId(null)}
      />
    </Card>
  );
}

function UsageFooter({ usage }) {
  if (!usage) return null;
  const cIds = usage.campaign_ids || [];
  const total = cIds.length + (usage.post_ids || []).length + (usage.landing_page_ids || []).length;
  return (
    <div className="mt-auto pt-2 text-xs text-muted-foreground flex items-center gap-3 border-t">
      <span data-testid="asset-usage-campaigns">{cIds.length} campaign{cIds.length === 1 ? "" : "s"}</span>
      {total === 0 && <span className="opacity-60">unused</span>}
    </div>
  );
}

/* ---------- Collections sub-tab ---------- */
function Collections() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);    // collection detail
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const role = (auth.getUser() || {}).role || "";
  const canWrite = ROLES_CAN_WRITE.has(role);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/asset-collections");
      setItems(r.data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newName.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      const r = await api.post("/asset-collections", { name: newName });
      toast.success("Collection created");
      setNewName(""); setCreateOpen(false);
      load();
      setOpen(r.data);
    } catch (e) {
      if (e?.response?.status !== 403) toast.error("Create failed");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this collection?")) return;
    try {
      await api.delete(`/asset-collections/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      if (e?.response?.status !== 403) toast.error("Delete failed");
    }
  };

  return (
    <Card className="mt-3">
      <CardContent className="pt-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Group assets together for campaign-wide reuse.
          </div>
          {canWrite && (
            <Button onClick={() => setCreateOpen(true)} data-testid="collections-create-open">
              + New collection
            </Button>
          )}
        </div>
        {loading ? (
          <div className="text-sm text-muted-foreground py-6">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-muted-foreground py-12 text-center" data-testid="collections-empty">
            No collections yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((c) => (
              <Card key={c.id} data-testid={`collection-card-${c.id}`} className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <button
                    type="button"
                    className="text-left font-medium hover:underline"
                    data-testid={`collection-open-${c.id}`}
                    onClick={() => setOpen(c)}
                  >
                    {c.name}
                  </button>
                  {canWrite && (
                    <Button size="sm" variant="ghost" data-testid={`collection-delete-${c.id}`}
                            onClick={() => del(c.id)}>×</Button>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {(c.asset_ids || []).length} asset{(c.asset_ids || []).length === 1 ? "" : "s"}
                </div>
              </Card>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="collection-create-dialog">
          <DialogHeader><DialogTitle>New collection</DialogTitle></DialogHeader>
          <Label>Name</Label>
          <Input data-testid="collection-name" value={newName} onChange={(e) => setNewName(e.target.value)} />
          <DialogFooter>
            <DialogClose asChild><Button variant="ghost">Abbrechen</Button></DialogClose>
            <Button disabled={busy} onClick={create} data-testid="collection-create-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CollectionDetail collection={open} onClose={() => setOpen(null)} onChanged={load} />
    </Card>
  );
}

function CollectionDetail({ collection, onClose, onChanged }) {
  const [members, setMembers] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const role = (auth.getUser() || {}).role || "";
  const canWrite = ROLES_CAN_WRITE.has(role);

  useEffect(() => {
    if (!collection) return;
    setLoading(true);
    api.get(`/asset-collections/${collection.id}`)
      .then(async (r) => {
        const ids = r.data.asset_ids || [];
        if (ids.length === 0) { setMembers([]); return; }
        const a = await api.get("/assets", { params: { limit: 100 } });
        const map = Object.fromEntries((a.data.items || []).map((x) => [x.id, x]));
        setMembers(ids.map((id) => map[id]).filter(Boolean));
      })
      .finally(() => setLoading(false));
  }, [collection?.id]);

  const removeMember = async (assetId) => {
    try {
      await api.delete(`/asset-collections/${collection.id}/assets/${assetId}`);
      setMembers((m) => m.filter((x) => x.id !== assetId));
      onChanged?.();
    } catch (e) {
      if (e?.response?.status !== 403) toast.error("Remove failed");
    }
  };

  // lazy-load picker only when opened to avoid circular imports
  const AssetPicker = require("./AssetPicker").default;

  const addMembers = async (ids) => {
    const current = new Set(members.map((m) => m.id));
    const add = ids.filter((i) => !current.has(i));
    if (!add.length) return;
    try {
      await api.post(`/asset-collections/${collection.id}/assets`, { asset_ids: add });
      toast.success(`Added ${add.length}`);
      // reload
      const a = await api.get("/assets", { params: { limit: 100 } });
      const map = Object.fromEntries((a.data.items || []).map((x) => [x.id, x]));
      setMembers([...members, ...add.map((i) => map[i]).filter(Boolean)]);
      onChanged?.();
    } catch (e) {
      if (e?.response?.status !== 403) toast.error("Add failed");
    }
  };

  if (!collection) return null;
  return (
    <Dialog open={!!collection} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="collection-detail" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{collection.name}</span>
            {canWrite && (
              <Button size="sm" onClick={() => setPickerOpen(true)} data-testid="collection-add-members">
                + Add assets
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="text-sm text-muted-foreground py-4">Loading…</div>
        ) : members.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center">No assets yet.</div>
        ) : (
          <div className="max-h-[420px] overflow-y-auto divide-y border rounded-md">
            {members.map((m) => (
              <div key={m.id} className="flex items-center gap-3 p-3" data-testid={`collection-member-${m.id}`}>
                {m.kind === "image" && m.preview_url ? (
                  <img src={m.preview_url} alt="" className="size-10 rounded object-cover border" />
                ) : (
                  <div className="size-10 grid place-items-center rounded border bg-muted/40 text-xs font-mono">
                    {m.kind.slice(0, 3)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{m.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{m.kind} · {m.status}</div>
                </div>
                {canWrite && (
                  <Button size="sm" variant="ghost" onClick={() => removeMember(m.id)} data-testid={`collection-remove-${m.id}`}>
                    Remove
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <DialogClose asChild><Button variant="ghost">Schließen</Button></DialogClose>
        </DialogFooter>

        <AssetPicker
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          value={members.map((m) => m.id)}
          onChange={addMembers}
        />
      </DialogContent>
    </Dialog>
  );
}
